/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { CheckOutlined, CloseOutlined } from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import type { ReactElement } from 'react'
import colors from '@sportninja/common/constants/appColors'
import type {
  StandingMilestone,
  StandingMilestoneState,
  TeamStanding,
} from '@sportninja/common/features/team-balance/utils/teamStanding'
import {
  paragraphSmall,
  paragraphXSmall,
  weightMedium,
  weightRegular,
  weightSemiBold,
} from 'src/components/css'
import { media } from 'src/components/Responsive'
import type { HeaderMoney } from '../utils/balanceTax'
import { balanceText } from '../utils/balanceI18n'
import {
  MILESTONE_DOT,
  milestoneColumns,
  trackLength,
  trackOffset,
} from '../utils/milestoneLayout'
import { milestoneCopy } from '../utils/standingCopy'
import type { StandingFormatters } from '../utils/standingCopy'
import { PRESENTATION } from './TeamPaymentStatusBadge'

/** The phone breakpoint (≤863px). `Responsive` is untyped JS, hence the cast. */
const MOBILE = (media as Record<string, string>).mobile

type PaymentMilestonesProps = {
  standing: TeamStanding
  money: HeaderMoney
  fmt: StandingFormatters
  now: Dayjs
}

/** How a label sits: from its dot's left edge, centred on it, or back from its right edge. */
type Align = 'start' | 'center' | 'end'

const labelAlignFor = (index: number, count: number): Align => {
  if (index === 0) {
    return 'start'
  }

  return index === count - 1 ? 'end' : 'center'
}

const flagAlignFor = (position: number): Align => {
  if (position < 12) {
    return 'start'
  }

  return position > 88 ? 'end' : 'center'
}

/**
 * SN-8310 (Jeff and James, Sep 22; design C2) — the league's payment dates as
 * milestones, each carrying its own date and the dollars due by then, with
 * Today flagged between them.
 *
 * Jeff on the runway: "I didn't know that this date was actually pointing to
 * here", and "if this is a timeline, you'd want to know what the qualified
 * date is." Every mark now says what it is, when, and how the team stands
 * against it, in one place. The spacing is even on purpose: it is a sequence of
 * gates, not a scale — Today alone is placed by time.
 *
 * Inon, Sep 24: always three marks — registration opens, registration ends,
 * the season starts — each named by its date, with the payments still in play
 * on it listed underneath; money already in is the mark's green check. Nothing
 * here repeats the banner, the chip or the figures row. Two marks on one date
 * say "Same day" on the line between them, since the even spacing would
 * otherwise put days between them.
 *
 * On desktop the line, its dots and the Today flag are a decorative band, and
 * the labels a grid under it (`milestoneLayout`) whose height follows its text.
 * On a phone the labels become a vertical list with their own dots. Either way
 * ONE ordered list, in date order with Today in its place, is what a screen
 * reader hears.
 */
const PaymentMilestones = ({
  standing,
  money,
  fmt,
  now,
}: PaymentMilestonesProps) => {
  const tierColor = PRESENTATION[standing.code].color
  const { milestones, todayPosition } = standing
  const last = milestones[milestones.length - 1]
  const pastLast = todayPosition > last.position
  const sameDayLabel = balanceText('common:balanceStandingSameDay')

  // Today's date alone: the days left or overdue are the chip's to say (Sep 24).
  const todayText = (
    <span css={todayLabelStyle}>
      {balanceText('common:balanceStandingToday', {
        date: fmt.day(now.format('YYYY-MM-DD')),
      })}
    </span>
  )

  // Today goes after the last mark it has passed, so the list reads in order.
  const items: ReactElement[] = []
  const todayItem = (
    <li key='today' css={todayItemStyle} data-testid='payment-milestones-today'>
      <span css={todayTickStyle} aria-hidden='true' />
      <span css={todayTextStyle}>{todayText}</span>
    </li>
  )
  let todayPlaced = false
  milestones.forEach((milestone, index) => {
    if (!todayPlaced && todayPosition < milestone.position) {
      items.push(todayItem)
      todayPlaced = true
    }
    const copy = milestoneCopy(milestone, standing, money, fmt)
    items.push(
      <li
        key={milestone.kind}
        css={[
          markItemStyle,
          labelAlignStyle[labelAlignFor(index, milestones.length)],
        ]}
        data-testid={`payment-milestone-${milestone.kind}`}
        data-state={milestone.state}
      >
        <span css={phoneDotStyle} aria-hidden='true'>
          <StepDot milestone={milestone} color={tierColor} />
        </span>
        <span css={markTextStyle}>
          <span css={titleStyle}>{copy.title}</span>
          <span css={dateStyle}>{copy.date}</span>
          {copy.lines.map((line) => (
            <span
              key={line.text}
              css={[
                amountStyle,
                line.state === 'upcoming' ? upcomingLineStyle : undefined,
              ]}
              style={{ color: lineColor(line.state, tierColor) }}
            >
              {line.text}
            </span>
          ))}
        </span>
      </li>
    )
  })
  if (!todayPlaced) {
    items.push(todayItem)
  }

  return (
    <div css={wrapStyle} data-testid='payment-milestones'>
      <div
        css={bandStyle}
        aria-hidden='true'
        data-testid='payment-milestones-band'
      >
        <span css={lineStyle} />
        <span
          css={elapsedStyle}
          style={{ width: trackLength(todayPosition) }}
        />
        {pastLast ? (
          <span css={tailStyle} style={{ left: trackOffset(last.position) }} />
        ) : null}
        {milestones.slice(1).map((milestone, i) =>
          milestone.day === milestones[i].day ? (
            <span
              key={`same-day-${milestone.kind}`}
              css={sameDayStyle}
              style={{
                left: trackOffset(
                  (milestones[i].position + milestone.position) / 2
                ),
              }}
              data-testid='payment-milestones-band-same-day'
            >
              {sameDayLabel}
            </span>
          ) : null
        )}
        {/*
          SN-8426 — Today's flag goes BEFORE the dots. Nothing here sets a
          z-index, so paint order is DOM order: painted last, the tick cut
          straight through a dot whenever Today fell on one ("Season started"
          on its own day). Painted here, it still crosses the line but passes
          behind an opaque dot.
        */}
        <span
          css={flagStyle}
          style={{ left: trackOffset(todayPosition) }}
          data-testid='payment-milestones-band-today'
        >
          <span
            css={[flagTextStyle, flagAlignStyle[flagAlignFor(todayPosition)]]}
          >
            {todayText}
          </span>
          <span
            css={flagTickStyle}
            data-testid='payment-milestones-band-tick'
          />
        </span>
        {milestones.map((milestone) => (
          <span
            key={milestone.kind}
            css={bandDotStyle}
            style={{ left: trackOffset(milestone.position) }}
            data-testid={`payment-milestones-band-dot-${milestone.kind}`}
          >
            <StepDot milestone={milestone} color={tierColor} />
          </span>
        ))}
      </div>
      <ol
        css={listStyle}
        style={{
          gridTemplateColumns: milestoneColumns(
            milestones.map((milestone) => milestone.position),
            pastLast
          ),
        }}
        aria-label={balanceText('common:balanceStandingTimelineLabel')}
      >
        {items}
      </ol>
    </div>
  )
}

export default PaymentMilestones

/** A payment's figure, in its own state's colour; one not yet in play is muted. */
const lineColor = (state: StandingMilestoneState, tierColor: string) => {
  switch (state) {
    case 'done':
      return colors.SUCCESS_GREEN
    case 'missed':
      return colors.ERROR_100
    case 'current':
      return tierColor
    default:
      return colors.NEUTRAL_50
  }
}

/**
 * Done: a green check. Missed: a red cross. Current: a ring in the status
 * colour. Passed: a steel check — a date gone by with nothing owed on it.
 */
const StepDot = ({
  milestone,
  color,
}: {
  milestone: StandingMilestone
  color: string
}) => {
  switch (milestone.state) {
    case 'done':
      return (
        <span css={[dotStyle, doneDotStyle]}>
          <CheckOutlined />
        </span>
      )
    case 'missed':
      return (
        <span css={[dotStyle, missedDotStyle]}>
          <CloseOutlined />
        </span>
      )
    case 'passed':
      return (
        <span css={[dotStyle, passedDotStyle]}>
          <CheckOutlined />
        </span>
      )
    case 'current':
      return (
        <span
          css={dotStyle}
          style={{
            border: `2px solid ${color}`,
            boxShadow: `0 0 0 4px ${color}40`,
          }}
        />
      )
    default:
      return <span css={[dotStyle, upcomingDotStyle]} />
  }
}

/*
 * The desktop band: the Today flag's one line ("Today · Sat, Sep 26") above,
 * then the line with its dots. Its height is fixed because nothing in it
 * wraps. The flag had a second line, the days left, until Sep 24 — the chip
 * under the pill says that now.
 */
const FLAG_HEIGHT = 20
const LINE_Y = FLAG_HEIGHT + MILESTONE_DOT / 2
const BAND_HEIGHT = FLAG_HEIGHT + MILESTONE_DOT

const wrapStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const bandStyle = css`
  position: relative;
  height: ${BAND_HEIGHT}px;

  ${MOBILE} {
    display: none;
  }
`

const lineStyle = css`
  position: absolute;
  left: ${MILESTONE_DOT / 2}px;
  right: ${MILESTONE_DOT / 2}px;
  top: ${LINE_Y - 2}px;
  height: 4px;
  border-radius: 2px;
  background: ${colors.SECONDARY_100};
`

/** Time passed, in neutral steel: time is neither good nor bad. */
const elapsedStyle = css`
  position: absolute;
  left: ${MILESTONE_DOT / 2}px;
  top: ${LINE_Y - 2}px;
  height: 4px;
  border-radius: 2px;
  background: ${colors.SECONDARY_75};
`

const tailStyle = css`
  position: absolute;
  right: ${MILESTONE_DOT / 2}px;
  top: ${LINE_Y - 2}px;
  height: 4px;
  background: repeating-linear-gradient(
    90deg,
    ${colors.NEUTRAL_100} 0 6px,
    transparent 6px 10px
  );
`

const bandDotStyle = css`
  position: absolute;
  top: ${FLAG_HEIGHT}px;
  transform: translateX(-50%);
`

/** Centred between its two marks, its foot 4px above the line. */
const sameDayStyle = css`
  ${paragraphXSmall};
  ${weightMedium};
  position: absolute;
  top: ${LINE_Y - 6}px;
  transform: translate(-50%, -100%);
  color: ${colors.NEUTRAL_70};
  white-space: nowrap;
`

/** A zero-width anchor at Today; its text hangs off it by `flagAlignStyle`. */
const flagStyle = css`
  position: absolute;
  top: 0;
  width: 0;
  height: ${LINE_Y + 10}px;
`

const flagTextStyle = css`
  ${paragraphXSmall};
  position: absolute;
  top: 0;
  width: max-content;
  display: flex;
  flex-direction: column;
`

const flagAlignStyle: Record<Align, ReturnType<typeof css>> = {
  start: css`
    left: -${MILESTONE_DOT / 2}px;
    text-align: left;
  `,
  center: css`
    left: 0;
    transform: translateX(-50%);
    text-align: center;
  `,
  end: css`
    right: -${MILESTONE_DOT / 2}px;
    text-align: right;
  `,
}

/**
 * From the foot of the flag's text down across the line, never up into the
 * text itself (SN-8426).
 */
const flagTickStyle = css`
  position: absolute;
  left: -1px;
  top: ${FLAG_HEIGHT}px;
  bottom: 0;
  width: 2px;
  border-radius: 1px;
  background: ${colors.NEUTRAL_0};
`

const listStyle = css`
  display: grid;
  align-items: start;
  margin: 0;
  padding: 0;
  list-style: none;

  ${MOBILE} {
    display: flex;
    flex-direction: column;
  }
`

/** In its grid column on desktop; plain left-aligned text on a phone. */
const labelAlignStyle: Record<Align, ReturnType<typeof css>> = {
  start: css`
    justify-self: start;
    text-align: left;
  `,
  center: css`
    justify-self: center;
    text-align: center;

    ${MOBILE} {
      text-align: left;
    }
  `,
  end: css`
    justify-self: end;
    text-align: right;

    ${MOBILE} {
      text-align: left;
    }
  `,
}

const markItemStyle = css`
  min-width: 0;

  ${MOBILE} {
    position: relative;
    display: grid;
    grid-template-columns: ${MILESTONE_DOT}px minmax(0, 1fr);
    gap: 12px;
    padding-bottom: 16px;

    /* The vertical connector, from under this dot to the next row. */
    &:not(:last-of-type)::before {
      content: '';
      position: absolute;
      left: ${MILESTONE_DOT / 2 - 1}px;
      top: ${MILESTONE_DOT}px;
      bottom: 0;
      width: 2px;
      background: ${colors.SECONDARY_100};
    }
  }
`

/** Each mark's own dot, for the phone list; the desktop dots live on the line. */
const phoneDotStyle = css`
  display: none;

  ${MOBILE} {
    display: block;
  }
`

const markTextStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

/**
 * Today in the list: read by a screen reader in its place among the marks, but
 * shown only on a phone — on desktop the flag on the line says it. Taken out
 * of the grid flow so it never occupies a mark's column.
 */
const todayItemStyle = css`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;

  ${MOBILE} {
    position: relative;
    width: auto;
    height: auto;
    overflow: visible;
    clip: auto;
    white-space: normal;
    display: grid;
    grid-template-columns: ${MILESTONE_DOT}px minmax(0, 1fr);
    gap: 12px;
    padding-bottom: 16px;

    /* Carries the connector through Today's row to the next mark. */
    &:not(:last-of-type)::before {
      content: '';
      position: absolute;
      left: ${MILESTONE_DOT / 2 - 1}px;
      top: 0;
      bottom: 0;
      width: 2px;
      background: ${colors.SECONDARY_100};
    }
  }
`

const todayTickStyle = css`
  display: none;

  ${MOBILE} {
    display: block;
    align-self: center;
    justify-self: center;
    width: 12px;
    height: 2px;
    background: ${colors.NEUTRAL_0};
  }
`

const todayTextStyle = css`
  ${paragraphXSmall};
  display: flex;
  flex-wrap: wrap;
  gap: 0 8px;
`

const todayLabelStyle = css`
  ${weightSemiBold};
  color: ${colors.NEUTRAL_0};
`

const dotStyle = css`
  width: ${MILESTONE_DOT}px;
  height: ${MILESTONE_DOT}px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 12px;
  background: ${colors.SECONDARY_300};
`

const doneDotStyle = css`
  background: ${colors.SUCCESS_200};
  color: ${colors.SECONDARY_500};
`

const missedDotStyle = css`
  background: ${colors.ERROR_300};
  color: ${colors.NEUTRAL_0};
`

const passedDotStyle = css`
  background: ${colors.SECONDARY_75};
  color: ${colors.SECONDARY_300};
`

const upcomingDotStyle = css`
  border: 2px solid ${colors.SECONDARY_100};
`

const titleStyle = css`
  ${paragraphSmall};
  ${weightSemiBold};
  color: ${colors.NEUTRAL_0};
`

const dateStyle = css`
  ${paragraphXSmall};
  color: ${colors.NEUTRAL_70};
`

const amountStyle = css`
  ${paragraphSmall};
  ${weightSemiBold};
`

/** Money not yet in play reads quieter than the payment due next. */
const upcomingLineStyle = css`
  ${weightRegular};
`
